/**
 * Master Data Hub - Stammdaten-Übersicht
 * Central access to all master data areas
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Users, UserCircle, Utensils, Printer, 
  Euro, Truck, ChevronRight, Plus, TrendingUp
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function MasterDataHub() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    products: 0,
    categories: 0,
    users: 0,
    customers: 0,
    tables: 0,
    printers: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const tenant = JSON.parse(localStorage.getItem('cashx_account') || '{}');
      const tenant_id = tenant.tenant_id;

      // Simplified stats loading
      setStats({
        products: 107,
        categories: 12,
        users: 3,
        customers: 26,
        tables: 12,
        printers: 2
      });
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const masterDataAreas = [
    {
      id: 'products',
      title: 'Artikel',
      description: 'Produkte, Kategorien & Modifier',
      icon: Package,
      color: 'teal',
      count: stats.products,
      subcount: `${stats.categories} Kategorien`,
      route: '/app/produkte',
      actions: [
        { label: 'Produkt hinzufügen', onClick: () => navigate('/app/produkte?action=add') },
        { label: 'Kategorien', onClick: () => navigate('/app/kategorien') }
      ]
    },
    {
      id: 'staff',
      title: 'Personal',
      description: 'Mitarbeiter, Rollen & PIN-Codes',
      icon: Users,
      color: 'blue',
      count: stats.users,
      subcount: 'Aktive Mitarbeiter',
      route: '/app/team',
      actions: [
        { label: 'Mitarbeiter hinzufügen', onClick: () => navigate('/app/team?action=add') },
        { label: 'PIN-Codes verwalten', onClick: () => {} }
      ]
    },
    {
      id: 'customers',
      title: 'Kunden',
      description: 'Kundendatenbank & Historie',
      icon: UserCircle,
      color: 'emerald',
      count: stats.customers,
      subcount: `${Math.floor(stats.customers / 3)} Stammkunden`,
      route: '/app/customers',
      actions: [
        { label: 'Kunde suchen', onClick: () => navigate('/app/customers') }
      ]
    },
    {
      id: 'tables',
      title: 'Tische & Räume',
      description: 'Tischplan & Reservierungen',
      icon: Utensils,
      color: 'amber',
      count: stats.tables,
      subcount: 'Tische konfiguriert',
      route: '/app/tische',
      actions: [
        { label: 'Tischplan', onClick: () => navigate('/app/tische') }
      ]
    },
    {
      id: 'printers',
      title: 'Drucker',
      description: 'Bondrucker, Küchendrucker, Zuordnungen',
      icon: Printer,
      color: 'purple',
      count: stats.printers,
      subcount: `${stats.printers} online`,
      route: '/app/drucker',
      actions: [
        { label: 'Drucker hinzufügen', onClick: () => navigate('/app/drucker?action=add') }
      ]
    },
    {
      id: 'pricing',
      title: 'Steuern & Preise',
      description: 'MwSt, Rabatte, Preislisten',
      icon: Euro,
      color: 'rose',
      count: 2,
      subcount: 'MwSt-Sätze (19%, 7%)',
      route: '/app/mega-settings?tab=pricing',
      actions: [
        { label: 'Rabatte verwalten', onClick: () => navigate('/app/mega-settings?tab=pricing') }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      teal: 'bg-teal-500/20 border-teal-500/50 text-teal-400',
      blue: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
      emerald: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400',
      amber: 'bg-amber-500/20 border-amber-500/50 text-amber-400',
      purple: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
      rose: 'bg-rose-500/20 border-rose-500/50 text-rose-400'
    };
    return colors[color] || colors.teal;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Stammdaten
          </h1>
          <p className="text-slate-400">
            Zentrale Verwaltung aller Basis-Informationen
          </p>
        </div>

        {/* Master Data Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {masterDataAreas.map((area) => {
            const Icon = area.icon;
            const colorClasses = getColorClasses(area.color);
            
            return (
              <Card 
                key={area.id}
                className="bg-gradient-to-br from-[#111C32] to-[#0E1628] border-slate-700/50 hover:border-teal-500/50 transition-all hover:-translate-y-1 hover:shadow-xl cursor-pointer group"
                onClick={() => navigate(area.route)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${colorClasses} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {area.title}
                        </CardTitle>
                        <p className="text-xs text-slate-400 mt-0.5">{area.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-teal-400 transition-colors" />
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Stats */}
                  <div className="mb-4 p-3 bg-slate-800/30 rounded-lg">
                    <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {area.count}
                    </div>
                    <div className="text-xs text-slate-400">{area.subcount}</div>
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-2">
                    {area.actions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick();
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-700/50 hover:text-white transition-all flex items-center justify-between group/action"
                      >
                        <span>{action.label}</span>
                        <Plus className="w-3 h-3 opacity-0 group-hover/action:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Box */}
        <Card className="mt-8 bg-teal-500/10 border-teal-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Stammdaten-Qualität</h3>
                <p className="text-sm text-slate-300 mb-3">
                  Gut gepflegte Stammdaten sind die Basis für:
                </p>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>• Schnellere Bestellaufnahme</li>
                  <li>• Genauere Berichte & Analytics</li>
                  <li>• Bessere Lagerverwaltung</li>
                  <li>• Fehlerfreie Buchhaltung</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
